import React from 'react';
import { SxProps, TextField } from '@mui/material';
import { sendApiRequest } from '../../lib/api/public';
import { Autocomplete, CircularProgress } from '@mui/material';
import { debounce } from '@mui/material/utils';
import notify from '../../lib/notify';

type JobsApiResponse = {
  list: Job[];
};

type AutocompleteService = {
  getJobsApiMethod: (input: string) => Promise<JobsApiResponse>;
};

const autocompleteService: { current: AutocompleteService | null } = { current: null };

export interface Job {
  _id: string;
  title: string;
  clientName: string;
  location?: string;
  locationType?: string;
  jobType?: string;
  jobTypeCustom?: string;
  recruiterId?: string;
  clientId?: string;
  salary?: string;
  attachments?: import('components/types').IAttachment[];
  notes?: string;
  description?: string;
  statusId?: string;
  customFieldsValues?: import('components/common/customFields/types').CustomFieldValues;
  autosourceCurrentCompanyFilter?: string;
  autosourcePastCompanyFilter?: string;
  autosourceFunctionFilter?: string;
  autosourceCurrentJobTitleFilter?: string;
  autosourceYearsInCurrrentCompanyFilter?: string;
  autosourceLocationFilter?: string;
  autosourceKeywordSearch?: string;
}

const getJobsApiMethod = (search: string) => {
  return sendApiRequest<JobsApiResponse>({
    path: `/jobs/search?search=${encodeURIComponent(search)}`,
    method: 'get',
  });
};

type Props = {
  onSelect: (job: Job | null) => void;
  disabled?: boolean;
  error?: boolean;
  label?: string;
  defaultValue?: Job;
  textFieldStyle?: React.CSSProperties;
  inputPropStyle?: React.CSSProperties;
  helperText?: string;
  sx?: SxProps;
};

export const jobLabelClientStyle = {
  display: 'inline-block',
  marginLeft: '5px',
  fontSize: '11px',
  color: '#666',
  fontStyle: 'italic',
};

function JobsAutoComplete({
  onSelect,
  disabled = false,
  defaultValue,
  textFieldStyle,
  inputPropStyle,
  helperText,
  error = false,
  label,
  sx,
}: Props) {
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState<Job | null>(defaultValue ?? null);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<Job[]>([]);

  React.useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue);
      setOptions([defaultValue]);
    }
  }, [defaultValue]);

  const fetch = React.useMemo(
    () =>
      debounce((request: { input: string }, callback: (results?: Job[]) => void) => {
        setLoading(true);
        const service = autocompleteService.current;

        if (!service) {
          setLoading(false);
          callback([]);
          return;
        }

        service
          .getJobsApiMethod(request.input)
          .then((data: JobsApiResponse) => {
            setLoading(false);
            callback(data.list || []);
          })
          .catch(error => {
            notify(error);
            setLoading(false);
            callback([]);
          });
      }, 400),
    [],
  );

  const setOptionsFromFetch = (results: Job[]) => {
    let newOptions: Job[] = [];

    if (value) {
      newOptions = [value];
    }

    if (results) {
      newOptions = [...newOptions, ...results];
    }

    setOptions(newOptions);
  };

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current) {
      autocompleteService.current = {
        getJobsApiMethod,
      };
    }

    fetch({ input: inputValue }, (results?: Job[]) => {
      if (active) {
        setOptionsFromFetch(results || []);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      sx={sx}
      filterOptions={options => options}
      getOptionKey={option => option._id}
      isOptionEqualToValue={(option, value) => option.title === value.title}
      renderOption={(props, option) => {
        const { key, ...restProps } = props as typeof props & { key: React.Key };
        return (
          <li key={key} {...restProps}>
            <span>{option.title}</span>
            <span style={jobLabelClientStyle}>{option.clientName}</span>
          </li>
        );
      }}
      options={options}
      autoComplete
      disabled={disabled}
      includeInputInList
      filterSelectedOptions
      value={value}
      getOptionLabel={option => option.title}
      noOptionsText="Type a job title ..."
      onChange={(_event, newValue: Job | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);

        onSelect(newValue);
      }}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={params => (
        <TextField
          {...params}
          id="job-auto-complete"
          size="small"
          label={label}
          sx={textFieldStyle}
          error={error}
          helperText={helperText}
          InputLabelProps={{
            ...params.InputLabelProps,
            shrink: true,
            ...(label
              ? {}
              : {
                  style: {
                    top: '-5px',
                  },
                }),
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
            style: inputPropStyle,
          }}
        />
      )}
    />
  );
}

export default JobsAutoComplete;
